import { Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { useDispatch, useSelector } from "react-redux";
import CustomCard from "components/CustomCard";
import { setShowLoading } from "store/sysSlice";
import { useNavigate } from "react-router-dom";
import { getEffectiveColor, groupBy, hasDeltaT } from "config/functions";
import CustomDataGrid from "components/customDataGrid";
import NullCheck from "components/NullCheck";
import { MC_SOFTWARE_TYPE } from "config/const";
import { ROLES_ID } from "config/roles";
import {
    BoltRounded,
    CategoryOutlined,
    FmdGoodOutlined,
    MarkEmailReadOutlined,
    MarkEmailUnreadOutlined,
    NotificationsNoneRounded,
    SettingsRemoteOutlined
} from "@mui/icons-material";
import useDataManager from "hooks/useDataManager";

const McMonitoring = ({ isMobile }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const { notificationReadBy } = useDataManager();
    const { bt } = useBizTypeTranslation();
    const list = useSelector((state) => state?.mc.mcList);
    const user = useSelector((state) => state.userSettings?.user);
    const notifications = useSelector((state) => state.notification?.list);
    const [data, setData] = useState(null)

    useEffect(() => {
        getData()
    }, [list, user, notifications]);

    const getData = async () => {
        if (user && list.length > 0) {
            let newList = [...list]

            if ((user?.userRole === ROLES_ID.DEPARTMENT_MANAGER ||
                user?.userRole === ROLES_ID.EMPLOYEE) &&
                user?.department?.mcList?.length > 0) {
                newList = newList?.filter(mc => user?.department?.mcList?.some(deptMc => deptMc === mc.id))
            }

            let currItem = { active: 0, notActive: 0, fault: 0, lowEffective: 0 }
            let groupByEffective = { 0: 0, 30: 0, 40: 0, 50: 0, 60: 0, 70: 0, 80: 0, 90: 0, 100: 0 }
            let hasMcDeltaT = false
            let hasMcB2 = false
            const groupByDevice = groupBy(newList, "deviceType")
            const groupByArea = groupBy(newList, "area.id")

            newList?.map(mc => {
                if (mc?.turnOnDate) currItem.active++
                if (mc?.turnOffDate) currItem.notActive++
                if (mc?.fault || !mc?.isConnected) currItem.fault++

                if (Number.isFinite(mc?.effective) && mc?.effective === 0) groupByEffective[0]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 30) groupByEffective[30]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 40) groupByEffective[40]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 50) groupByEffective[50]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 60) groupByEffective[60]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 70) groupByEffective[70]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 80) groupByEffective[80]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 90) groupByEffective[90]++
                if (Number.isFinite(mc?.effective) && mc?.effective === 100) groupByEffective[100]++
                if (Number.isFinite(mc?.effective) && mc?.effective < 60) currItem.lowEffective++

                if (hasDeltaT(mc)) hasMcDeltaT = true
                if (mc?.softwareType === MC_SOFTWARE_TYPE.B2_0) hasMcB2 = true
            })


            const obj = {
                hasMcEffective: hasMcDeltaT,
                hasMcB2: hasMcB2,
                notifications: notifications
                    ?.filter(n => n?.mc?.guid)
                    ?.sort((a, b) => new Date(b.created) - new Date(a.created))
                    ?.slice(0, 10) || [],

                byDevices: Object.keys(groupByDevice)?.map((key, idx) => ({
                    id: idx,
                    deviceType: key,
                    length: groupByDevice[key]?.length,
                })),

                byArea: Object.keys(groupByArea)?.map((key, idx) => ({
                    id: groupByArea[key][0]?.area?.id || "1",
                    name: groupByArea[key][0]?.area?.name || bt("NONE"),
                    length: groupByArea[key]?.length,
                })),

                byEffective: Object.keys(groupByEffective)?.map((key, idx) => ({
                    id: idx,
                    name: key,
                    length: groupByEffective[key],
                })),

                status: [
                    {
                        id: 'active',
                        name: "DEVICE_ACTIVE",
                        status: 1,
                        show: true,
                        value: currItem.active,
                        iconBgColor: "#d9efff",
                        iconColor: "#0091ff"
                    },
                    {
                        id: 'not-Active',
                        name: "DEVICE_NOT_ACTIVE",
                        status: 2,
                        show: true,
                        value: currItem.notActive,
                        iconBgColor: "#e4e4e4",
                        iconColor: "#4a4a4a"
                    },
                    {
                        id: 'fault',
                        name: "DEVICE_FAULT",
                        status: 3,
                        show: true,
                        value: currItem.fault,
                        iconBgColor: "#ffe1d9",
                        iconColor: "#ff3501"
                    },
                    {
                        id: 'low-effective',
                        name: "DEVICE_LOW_EFFECTIVE",
                        status: 4,
                        show: hasMcDeltaT,
                        value: currItem.lowEffective,
                        iconBgColor: "#fff2d9",
                        iconColor: "#ffaa01"
                    },
                ]
            }

            setData(obj)
        }
        dispatch(setShowLoading(false))
    }

    if (!data) return

    return (
        <Stack spacing={2}>
            <Stack
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
                direction='row'
                justifyContent={{ xs: "center", md: "start" }}
                spacing={{ xs: 1, md: 2 }}
            >

                {data?.status?.filter(i => i?.show)?.map((st, idx) => {
                    return (
                        <CustomCard
                            key={idx}
                            alignItems="center"
                            direction="row"
                            width={{ xs: "48%", md: 220 }}
                            sx={{ cursor: "pointer" }}
                            onClick={() => navigate(`/Control-And-Monitoring/status/${st?.status}`)}
                        >
                            <Stack
                                width={{ xs: 50, md: 70 }}
                                height={{ xs: 50, md: 70 }}
                                bgcolor={st?.iconBgColor}
                                borderRadius={10}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <SettingsRemoteOutlined sx={{ color: st?.iconColor }} />
                            </Stack>

                            <Stack padding={{ xs: 0, md: 1 }}>
                                <Typography fontSize={{ xs: 12, md: 14 }}>
                                    {bt(st?.name)}
                                </Typography>
                                <Typography fontSize={{ xs: 20, md: 24 }} fontWeight="bold">
                                    {st?.value}
                                </Typography>
                            </Stack>
                        </CustomCard>
                    )
                })}

            </Stack>

            <Stack
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
                direction='row'
                justifyContent={{ xs: "center", md: "start" }}
                spacing={2}
            >

                <CustomCard width={{ xs: "100%", md: "auto" }}>
                    <Stack direction="row" spacing={1} >
                        <CategoryOutlined sx={{ color: "primary.main" }} />
                        <Typography>{bt('DEVICES')}</Typography>
                    </Stack>
                    <CustomDataGrid
                        hideHeader
                        hideFooter
                        hideScroll
                        setItem={(mc) => navigate(`/Control-And-Monitoring/device/${mc?.deviceType}`)}
                        maxHeight={250}
                        rowHeight={40}
                        borderRadius="10px 10px 10px 10px"
                        columns={[
                            {
                                field: "deviceType",
                                headerName: bt("DEVICE_TYPE"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => bt(value?.row?.deviceType)
                            },
                            {
                                field: "length",
                                headerName: bt("LENGTH"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => `${bt(value?.row?.length)} ${bt('UNITS')}`
                            },
                        ]}
                        list={data?.byDevices}
                    />
                </CustomCard>

                <CustomCard width={{ xs: "100%", md: "auto" }}>
                    <Stack direction="row" spacing={1} >
                        <FmdGoodOutlined sx={{ color: "primary.main" }} />
                        <Typography>{bt('AREAS')}</Typography>
                    </Stack>
                    <CustomDataGrid
                        hideHeader
                        hideFooter
                        hideScroll
                        setItem={(area) => navigate(`/Control-And-Monitoring/area/${area?.id}`)}
                        maxHeight={250}
                        rowHeight={40}
                        borderRadius="10px 10px 10px 10px"
                        columns={[
                            {
                                field: "name",
                                headerName: bt("NAME"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => value?.row?.name
                            },
                            {
                                field: "length",
                                headerName: bt("LENGTH"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => `${bt(value?.row?.length)} ${bt('UNITS')}`
                            },
                        ]}
                        list={data?.byArea}
                    />
                </CustomCard>

            </Stack>

            <Stack
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
                direction='row'
                justifyContent={{ xs: "center", md: "start" }}
                spacing={2}
            >

                <NullCheck valueForCheck={data?.hasMcEffective}>
                    <CustomCard width={{ xs: "100%", md: "auto" }}>
                        <Stack direction="row" spacing={1} >
                            <BoltRounded sx={{ color: "primary.main" }} />
                            <Typography>{bt('EFFECTIVE')}</Typography>
                        </Stack>
                        <CustomDataGrid
                            hideHeader
                            hideFooter
                            hideScroll
                            maxHeight={400}
                            rowHeight={40}
                            borderRadius="10px 10px 10px 10px"
                            columns={[
                                {
                                    field: "name",
                                    headerName: bt("NAME"),
                                    width: isMobile ? 150 : 200,
                                    renderCell: (value) => {
                                        return (
                                            <Stack height="100%" justifyContent="center">
                                                <Typography color={getEffectiveColor(parseInt(value?.row?.name))} variant="body2">
                                                    {`${value?.row?.name}%`}
                                                </Typography>
                                            </Stack>
                                        )
                                    }
                                },
                                {
                                    field: "length",
                                    headerName: bt("LENGTH"),
                                    width: isMobile ? 150 : 200,
                                    renderCell: (value) => `${bt(value?.row?.length)} ${bt('UNITS')}`
                                },
                            ]}
                            list={data?.byEffective?.filter(i => i?.name !== "undefined")}
                        />
                    </CustomCard>
                </NullCheck>

                <CustomCard width={{ xs: "100%", md: "auto" }} >

                    <Stack direction="row" spacing={1} >
                        <NotificationsNoneRounded sx={{ color: "primary.main" }} />
                        <Typography>{bt('TEN_NOTIFICATIONS')}</Typography>
                    </Stack>

                    <CustomDataGrid
                        hideHeader
                        hideFooter
                        hideScroll
                        setItem={(item) => notificationReadBy(item, user, notifications)}
                        maxHeight={400}
                        rowHeight={40}
                        borderRadius="10px 10px 10px 10px"
                        columns={[
                            {
                                field: "name",
                                headerName: bt("NAME"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => {
                                    return (
                                        <Stack height="100%" direction="row" alignItems="center" spacing={1}>
                                            {value?.row?.readByUser
                                                ? <MarkEmailReadOutlined fontSize="small" sx={{ color: "success.main" }} />
                                                : <MarkEmailUnreadOutlined fontSize="small" sx={{ color: "error.text" }} />
                                            }
                                            <Typography variant="body2">
                                                {value?.row?.mc?.name}
                                            </Typography>
                                        </Stack>
                                    )
                                }
                            },
                            {
                                field: "title",
                                headerName: bt("TITLE"),
                                width: isMobile ? 150 : 200,
                                renderCell: (value) => bt(value?.row?.title)
                            },
                        ]}
                        list={data?.notifications?.map(item => ({
                            ...item,
                            readByUser: item?.readBy?.some(i => i?.employee?.id === user?._id),
                            title: bt(item.title)
                        }))}
                    />
                </CustomCard>
            </Stack>
        </Stack >
    )
}
export default McMonitoring